import { Table, Spin, Alert, Tag, Tooltip } from 'antd';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useGetLocationOwnerForOperator } from './Hook/Index';
import { useOperatorStore } from '../../store/useOperatorStore';
import { unixToLocalDate } from '../../utils/CommonFunctions';

export default function LocationOwnerList() {
  const { t } = useTranslation('LocationOwners');
  const { operatorId } = useOperatorStore(); // Get operatorId from store
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Allow page size to change

  const { data, isLoading, error } = useGetLocationOwnerForOperator({
    operatorId,
    page: currentPage, // Convert page number to offset
    pageSize: pageSize,
  });

  // Extract and map data, filtering only "LOCATION_OWNER"
  const locationOwners = useMemo(() => {
    return (
      data?.paginatedData
        ?.filter((item) => item.role === 'LOCATION_OWNER') // Filter by role
        .map((item) => ({
          key: item._id,
          _id: item._id,
          ownerName: `${item.fname || ''} ${item.lname || ''}`.trim(),
          email: item.email,
          phone: item.phone_code
            ? `+${item.phone_code} ${item.phone || ''}`
            : item.phone || t('notAvailable'),
          role: item.role,
          status: item.status,
          isPasswordChanged: item.isPasswordChanged,
          preferredLanguage: item.preferredLanguage,
          businessName: item.businessName || t('notAvailable'),
          taxNumber: item.taxNumber || t('notAvailable'),
          createdBy: item.created_by,
          updatedBy: item.updated_by,
          createdAt: item.created_at,
          updatedAt: item.updated_at,
          lastLogin: item.lastLogin,
        })) || []
    );
  }, [data?.paginatedData, t]);

  // Get total count - use API totalCount if available, otherwise calculate from filtered data
  const totalCount = useMemo(() => {
    if (data?.totalCount !== undefined) {
      // If API provides totalCount, use it (assuming it's already filtered)
      return data.totalCount;
    }
    // Otherwise, calculate from current page data (not ideal for pagination, but works)
    return data?.paginatedData?.filter((item) => item.role === 'LOCATION_OWNER').length || 0;
  }, [data?.totalCount, data?.paginatedData]);

  // Status color mapping
  const getStatusColor = (status) => {
    const statusUpper = status?.toUpperCase();
    switch (statusUpper) {
      case 'ACTIVE':
        return 'green';
      case 'BLOCKED':
        return 'red';
      case 'INACTIVE':
        return 'orange';
      default:
        return 'default';
    }
  };

  // Define columns dynamically
  const columns = useMemo(
    () => [
      {
        title: t('ownerName'),
        dataIndex: 'ownerName',
        key: 'ownerName',
        align: 'center',
        ellipsis: true,
        render: (text) => (
          <Tooltip title={text}>
            <span className="font-medium">{text || t('notAvailable')}</span>
          </Tooltip>
        ),
      },
      {
        title: t('email'),
        dataIndex: 'email',
        key: 'email',
        align: 'center',
        ellipsis: true,
        responsive: ['md'],
        render: (text) => (
          <Tooltip title={text}>
            <span>{text || t('notAvailable')}</span>
          </Tooltip>
        ),
      },
      {
        title: t('phone'),
        dataIndex: 'phone',
        key: 'phone',
        align: 'center',
        ellipsis: true,
        responsive: ['lg'],
        render: (text) => (
          <Tooltip title={text}>
            <span>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: t('status'),
        dataIndex: 'status',
        key: 'status',
        align: 'center',
        render: (status) => (
          <Tag color={getStatusColor(status)} className="capitalize font-medium" variant="outlined">
            {status || t('notAvailable')}
          </Tag>
        ),
      },
      {
        title: t('businessName'),
        dataIndex: 'businessName',
        key: 'businessName',
        align: 'center',
        ellipsis: true,
        responsive: ['xl'],
        render: (text) => (
          <Tooltip title={text}>
            <span>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: t('taxNumber'),
        dataIndex: 'taxNumber',
        key: 'taxNumber',
        align: 'center',
        ellipsis: true,
        responsive: ['xl'],
        render: (text) => (
          <Tooltip title={text}>
            <span>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: t('createdAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        align: 'center',
        responsive: ['lg'],
        render: (timestamp) => (
          <span className="whitespace-nowrap">
            {timestamp ? unixToLocalDate(timestamp) : t('notAvailable')}
          </span>
        ),
      },
      {
        title: t('lastLogin'),
        dataIndex: 'lastLogin',
        key: 'lastLogin',
        align: 'center',
        responsive: ['xl'],
        render: (timestamp) => (
          <span className="whitespace-nowrap">
            {timestamp ? unixToLocalDate(timestamp) : t('notAvailable')}
          </span>
        ),
      },
    ],
    [t]
  );

  if (error) {
    return (
      <div className="mt-8">
        <Alert message={t('error')} type="error" showIcon />
      </div>
    );
  }

  return (
    <div className="mt-8">
      <Table
        columns={columns}
        dataSource={locationOwners}
        rowKey="key"
        loading={isLoading}
        variant="outlined"
        size="middle"
        scroll={{ x: 1400, y: '67vh' }}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          position: ['bottomCenter'],
          onChange: (page, size) => {
            setCurrentPage(page);
            if (size) setPageSize(size);
          },
          onShowSizeChange: (current, size) => {
            setCurrentPage(1);
            setPageSize(size);
          },
          showTotal: (total, range) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
        locale={{ emptyText: t('noLocationOwnersFound') }}
      />
    </div>
  );
}
